import React, { useCallback, useEffect, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';

import { ChallengeDescriptionSectionRequest } from 'src/types/shared/Challenge';
import { tinyMCEPlugins, tinyMCEToolbar } from '@constants/tinyMCEConfig';

import ActionButton from 'lib/src/components/button/ActionButton';
import TextInput from '@components/form/TextInput';
import FormField from 'lib/src/components/form/FormField';
import useFieldValidation from '@components/form/hooks/useFieldValidation';
import ButtonRow from 'lib/src/components/button/ButtonRow';

import usePrevious from 'lib/src/hooks/usePrevious';

// Helper function to get text content without HTML tags
const getTextContent = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const WYSIWYGInput: React.FC<WYSIWYGInputProps> = ({
    value,
    onChange,
    handleDelete,
    handleMoveSection,
    index,
    shouldShowSortButton,
    canMoveUp,
    canMoveDown,
}) => {
    const maxLength = 15000;

    const [error, showError] = useFieldValidation({
        name: `descriptionSections[${index}]`,
        value,
    });

    const [contentRemainingLength, setContentRemainingLength] = useState<number>(
        maxLength - getTextContent(value.contents[0].content).length,
    );
    const [contentValue, setContentValue] = useState<string>(value.contents[0].content);

    const prevContent = usePrevious(value.contents[0].content);

    const _onChange = useCallback(
        (name: string, content: string) => {
            if (name === `wysiwygInput-${index}`) {
                const textContent = getTextContent(content);
                const charsRemaining = maxLength - textContent.length;

                setContentRemainingLength(charsRemaining);

                onChange(index, {
                    ...value,
                    contents: [{ ...value.contents[0], content }],
                });
            } else if (name === `wysiwygInput-title-${index}`)
                onChange(index, { ...value, title: content });
            else if (name === `wysiwygInput-subtitle-${index}`)
                onChange(index, { ...value, contents: [{ ...value.contents[0], title: content }] });
        },
        [index, onChange, value],
    );

    useEffect(() => {
        if (value.contents[0].content !== prevContent) {
            setContentValue(value.contents[0].content);
        }
    }, [value, prevContent]);

    useEffect(() => {
        if (prevContent !== contentValue) {
            _onChange(`wysiwygInput-${index}`, contentValue);
        }
    }, [_onChange, index, prevContent, contentValue]);

    useEffect(() => {
        if (!!value.contents[0].content.length) {
            const textLength = getTextContent(value.contents[0].content).length;
            setContentRemainingLength(maxLength - textLength);
        } else {
            setContentRemainingLength(maxLength);
        }
    }, [value.contents]); // Set chars remaining on mount if value exists

    return (
        <div className={`db-input wysiwyg`} key={index || undefined}>
            <label>WYSIWYG Input</label>
            <div className={`input-container ${error ? 'error' : ''}`}>
                <FormField name={`descriptionSections[${index}]`} error={error}>
                    <label htmlFor={`wysiwygInput-title-${index}`}>Section title</label>
                    <TextInput
                        name={`wysiwygInput-title-${index}`}
                        value={value.title}
                        placeholder="Section Title"
                        onChange={_onChange}
                    />
                    <label htmlFor={`wysiwygInput-title-${index}`}>WYSIWYG title</label>
                    <TextInput
                        name={`wysiwygInput-subtitle-${index}`}
                        value={value.contents[0].title}
                        placeholder="WYSIWYG Title"
                        onChange={_onChange}
                    />
                    <Editor
                        tinymceScriptSrc="/tinymce/tinymce.min.js"
                        licenseKey="gpl"
                        onEditorChange={(value: any) => {
                            // replacing empty h1 that tinymce adds with br elements
                            const fixedValue = value.replace(
                                /<h1><strong>&nbsp;<\/strong><\/h1>/g,
                                '<br />',
                            );

                            // Truncate to max length of 15000 visible characters
                            const textContent = getTextContent(fixedValue);
                            if (textContent.length <= maxLength) {
                                setContentValue(fixedValue);
                            } else {
                                alert(
                                    `Content exceeds the maximum length of ${maxLength} characters.`,
                                );
                            }
                        }}
                        onBlur={showError}
                        onInit={(_: unknown, editor: any) => {
                            editor.setContent(contentValue);
                        }}
                        value={contentValue}
                        init={{
                            height: 500,
                            menubar: false,
                            plugins: tinyMCEPlugins,
                            forced_root_block: '',
                            force_br_newlines: true,
                            force_p_newlines: false,
                            max_chars: maxLength,
                            // paste_as_text: true, // Ensure the pasted content is in plain text initially
                            paste_preprocess: (plugin, args) => {
                                // Handle the paste event by sanitizing content
                                const sanitizedContent = sanitizeHTML(args.content);
                                args.content = sanitizedContent;
                            },
                            toolbar: tinyMCEToolbar,
                            content_style: '* { margin: 5px 0; }',
                        }}
                    />
                    <p className="charsremaining">{contentRemainingLength} characters remaining</p>
                    <ButtonRow>
                        {!!handleMoveSection && shouldShowSortButton && (
                            <>
                                <ActionButton
                                    type="button"
                                    onClick={() => handleMoveSection(index, -1)}
                                    color="grey"
                                    icon="chevron-up"
                                    disabled={!canMoveUp}
                                >
                                    Move up
                                </ActionButton>
                                <ActionButton
                                    type="button"
                                    onClick={() => handleMoveSection(index, 1)}
                                    color="grey"
                                    icon="chevron-down"
                                    disabled={!canMoveDown}
                                >
                                    Move down
                                </ActionButton>
                            </>
                        )}
                        {!!handleDelete && (
                            <ActionButton
                                type="button"
                                color="red"
                                onClick={() => handleDelete(index)}
                            >
                                Delete Section
                            </ActionButton>
                        )}
                    </ButtonRow>
                </FormField>
            </div>
        </div>
    );
};

function sanitizeHTML(inputHTML: string): string {
    const div = document.createElement('div');
    div.innerHTML = inputHTML;

    // Returning if it is not HTML that has been pasted
    if (!div.querySelectorAll('*').length) return inputHTML;

    function hasMeaningfulContent(element: HTMLElement): boolean {
        if (element.nodeName.toLowerCase() === 'img') return true; // Images are always meaningful

        const text = element.textContent?.trim();
        if (text && text === '&nbsp;') return false;
        if (text && text !== '\u00A0') return true; // Has visible text

        return Array.from(element.children).some(child =>
            hasMeaningfulContent(child as HTMLElement),
        );
    }

    function cleanElement(element: HTMLElement) {
        Array.from(element.children).forEach(child => cleanElement(child as HTMLElement));

        // If the element doesn't have meaningful content, remove it
        if (!hasMeaningfulContent(element) && element.nodeName.toLowerCase() !== 'br') {
            element.remove();
            return;
        }

        // Remove excessive <br>, keeping just **one** consecutive <br>
        element.innerHTML = element.innerHTML.replace(/(<br\s*\/?>){2,}/g, '<br />');

        // Retain <br> elements only if they have a sibling with meaningful content
        Array.from(element.children).forEach(child => {
            if (child.nodeName.toLowerCase() === 'br') {
                const previousSiblingHasContent =
                    child.previousElementSibling &&
                    hasMeaningfulContent(child.previousElementSibling as HTMLElement);
                const nextSiblingHasContent =
                    child.nextElementSibling &&
                    hasMeaningfulContent(child.nextElementSibling as HTMLElement);

                if (!previousSiblingHasContent && !nextSiblingHasContent) {
                    child.remove(); // Remove <br> if neither sibling has meaningful content
                }
            }
        });

        // Remove disallowed attributes
        Array.from(element.attributes).forEach(attr => {
            if (attr.name === 'style' || attr.name.startsWith('id') || attr.name === 'dir') {
                element.removeAttribute(attr.name);
            }
        });
    }

    function convertToLists(container: HTMLElement) {
        const paragraphs = Array.from(container.querySelectorAll('p'));
        let ul: HTMLUListElement | null = null;

        paragraphs.forEach(p => {
            const text = p.textContent?.trim();

            // Detects MS Word-style bullets (· or •)
            if (text && /^·|•|·/.test(text)) {
                if (!ul) {
                    ul = document.createElement('ul');
                    p.parentNode?.insertBefore(ul, p);
                }

                const li = document.createElement('li');
                li.textContent = text.replace(/^·|•|·/gm, ''); // Remove bullet symbol
                ul.appendChild(li);
                p.remove();
            } else {
                ul = null; // Reset if the paragraph is not a list item
            }
        });
    }

    // Perform the cleaning
    div.querySelectorAll('*').forEach(element => cleanElement(element as HTMLElement));

    // Convert lists
    convertToLists(div);

    const htmlString = div.innerHTML.replaceAll(/^·|•|·/gm, '');

    return htmlString;
}

interface WYSIWYGInputProps {
    value: ChallengeDescriptionSectionRequest;
    onChange: (index: number, newData: ChallengeDescriptionSectionRequest) => void;
    handleDelete?: (index: number) => void;
    handleMoveSection?: (index: number, moveBy: number) => void;
    index: number;
    shouldShowSortButton: boolean;
    API_URL?: string;
    S3_URL?: string;
    canMoveUp: boolean;
    canMoveDown: boolean;
}

export default WYSIWYGInput;
